/**
 * ImpactHighlightsManager.tsx - Impact Highlights Management Component
 *
 * This component provides an interface for managing impact highlights in the
 * fundraiser builder with drag-and-drop reordering and inline editing.
 *
 * Key Features:
 * - Impact highlight creation and editing
 * - Drag-and-drop reordering with visual feedback
 * - Icon selection with color customization
 * - Inline editing with real-time validation
 * - Add, edit, and delete functionality
 * - Preview of impact highlights as configured
 *
 * Impact Highlight Fields:
 * - Icon: Selectable from Lucide icon library
 * - Color: Custom color picker for icon theming
 * - Number/Metric: Statistical value or achievement
 * - Description: Explanatory text for the metric
 * - Order: Drag-and-drop positioning
 *
 * Management Features:
 * - Add new impact highlights with defaults
 * - Edit existing highlights inline
 * - Delete highlights with confirmation
 * - Reorder using drag-and-drop interface
 * - Color picker integration for theming
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, drag-and-drop library, icon selector, color picker
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Plus, X, GripVertical, Palette } from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { IconSelectorModal } from "./IconSelectorModal";
import {
  FlexContainer,
  ColorPicker as ColorPickerComponent,
  IColor,
} from "@mindmemobile/mm-shared-global";
import {
  ColorTextInput,
  SelectedColorButton,
} from "../gallery-settings/GalleryAppearanceSection";
import { ColorModel, CreateFundraiserModel } from "../../models";
import { ImpactModel } from "../../models/FundraisersGridModels";
import { withAlpha } from "../../utils";

const ImpactContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  .checkout-color-picker {
    position: fixed;
    top: auto;
    left: auto;
    transform: translate(54px, 176px);
    z-index: 9999 !important;
    #rbgcp-wrapper {
      // this is for the height width of the color picker box inside the color picker component
      > div:first-child > div:nth-child(2) > div:nth-child(2) {
        height: 150px !important;
      }
      > div:first-child > div:first-child {
        height: 165px !important;
      }
    }
  }
`;

const ImpactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 16px;
  width: 50%;
  align-self: flex-start;
`;

const ImpactCard = styled.div<{
  isEditing: boolean;
  isDragging: boolean;
  error: boolean;
}>`
  width: 100%;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};

  border-color: ${(props) => (props.error ? "#dc2626" : "")};

  &:hover {
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  ${(props) =>
    props.isEditing &&
    `
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  `}
`;

const ImpactMainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const DragHandle = styled.div`
  cursor: grab;
  color: #94a3b8;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #f1f5f9;
    color: #64748b;
  }

  &:active {
    cursor: grabbing;
  }
`;

const IconPreview = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ color }) => (color ? withAlpha(color, 0.1) : color)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ color }) => color};
  flex-shrink: 0;
  box-shadow: 0 2px 8px
    ${({ color }) => (color ? withAlpha(color, 0.12) : color)};
`;

const PreviewContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PreviewNumber = styled.div<{ color: string }>`
  font-size: 24px;
  font-weight: 800;
  color: ${(props) => props.color};
  line-height: 1;
`;

const PreviewDescription = styled.div<{ color: string }>`
  font-size: 14px;
  color: ${(props) => props.color};
  font-weight: 600;
  line-height: 1.2;
`;

const RemoveButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #fecaca;
    transform: scale(1.05);
  }
`;

const EditingSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EditingTopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const EditingBottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #3b82f6;
  }
`;

const IconSelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const ColorInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorPicker = styled.input`
  width: 40px;
  height: 40px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  background: transparent;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const ColorInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 12px;
  color: #1e293b;
  font-family: monospace;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const AddImpactButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
  min-height: 100px;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #f8fafc;
    transform: translateY(-1px);
  }
`;

const AddImpactText = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const DropIndicator = styled.div<{ isActive: boolean }>`
  height: 3px;
  background: ${(props) => (props.isActive ? "#3b82f6" : "transparent")};
  border-radius: 2px;
  transition: all 0.2s ease;
  margin: 4px 0;
`;

interface Impact {
  id: string;
  icon: string;
  color: string;
  number: string;
  description: string;
  order: number;
}

interface ImpactManagerProps {
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

const availableIcons = [
  // Core Impact Icons
  "Heart",
  "Users",
  "Globe",
  "Target",
  "TrendingUp",
  "Award",
  "Gift",
  "Star",
  "CheckCircle",
  "Zap",
  "Shield",
  "Sparkles",

  // Health & Medical Impact
  "Stethoscope",
  "Cross",
  "Activity",
  "Plus",
  "ShieldCheck",
  "Pill",

  // Education Impact
  "GraduationCap",
  "BookOpen",
  "School",
  "PenTool",
  "Lightbulb",

  // Environmental Impact
  "TreePine",
  "Leaf",
  "Droplets",
  "Sun",
  "Recycle",
  "Sprout",

  // Community Impact
  "Home",
  "Building",
  "Handshake",
  "PartyPopper",

  // Animals & Nature
  "PawPrint",
  "Bird",
  "Fish",
  "Flower",
  "Trees",

  // Food & Nutrition
  "Apple",
  "UtensilsCrossed",
  "Wheat",
  "ChefHat",

  // General Impact
  "Trophy",
  "Medal",
  "Flag",
  "Rocket",
  "Crown",
];

export const ImpactManager: React.FC<ImpactManagerProps> = observer(
  ({ onKeyDown }) => {
    const { builderData } = fundraiserStore;
    const [editingImpact, setEditingImpact] = useState<number | null>(null);
    const [showIconSelector, setShowIconSelector] = useState(false);
    const [iconSelectorIndex, setIconSelectorIndex] = useState<number | null>(
      null
    );
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dropIndex, setDropIndex] = useState<number | null>(null);
    const [showImpactColorPicker, setShowImpactColorPicker] =
      useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const impacts = builderData.impact || [];

    // Close expanded impact when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setEditingImpact(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const addImpact = () => {
      const newImpact: Impact = {
        id: Date.now().toString(),
        icon: "Target",
        color: "#3b82f6",
        number: "",
        description: "",
        order: impacts.length,
      };
      fundraiserStore.updateBuilderField("impact", [...impacts, newImpact]);
      setEditingImpact(impacts.length); // open the newly added one
    };

    const removeImpactByIndex = (index: number) => {
      const updated = impacts.filter((_, i) => i !== index);
      const reordered = updated.map((item, idx) => ({ ...item, order: idx }));
      fundraiserStore.updateBuilderField("impact", reordered);
    };

    const updateImpact = (
      index: number,
      field: string,
      value: string | IColor
    ) => {
      const updated = impacts.map((item, i) =>
        i === index ? new ImpactModel({ ...item, [field]: value }) : item
      );
      console.log(field, value);
      fundraiserStore.updateBuilderField("impact", updated);
    };

    const handleIconSelect = (index: number, iconName: string) => {
      updateImpact(index, "icon", iconName);
      setEditingImpact(index);
      setShowIconSelector(false);
      setIconSelectorIndex(null);
    };

    const openIconSelector = (index: number) => {
      setIconSelectorIndex(index);
      setShowIconSelector(true);
    };

    const handleCardClick = (e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      setEditingImpact(editingImpact === index ? null : index);
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
      e.stopPropagation();
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("index", index.toString());
    };

    const handleDragEnd = (e: React.DragEvent) => {
      e.stopPropagation();
      setDraggedIndex(null);
      setDropIndex(null);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedIndex !== null) setDropIndex(index);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget as Node))
        setDropIndex(null);
    };

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      const draggedIndex = Number(e.dataTransfer.getData("index"));
      if (isNaN(draggedIndex) || draggedIndex === targetIndex) return;

      const reordered = [...impacts];
      const [draggedImpact] = reordered.splice(draggedIndex, 1);

      const adjustedIndex =
        draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
      reordered.splice(adjustedIndex, 0, draggedImpact);

      const updated = reordered.map((item, index) => ({
        ...item,
        order: index,
      }));
      fundraiserStore.updateBuilderField("impact", updated);

      setDraggedIndex(null);
      setDropIndex(null);
    };

    const renderIcon = (iconName: string, color: string) => {
      const Icon = (Icons as any)[iconName];
      return Icon ? <Icon size={20} /> : <Icons.Target size={20} />;
    };

    return (
      <ImpactContainer ref={containerRef}>
        <ImpactGrid>
          {impacts.map((impact, index) => (
            <React.Fragment key={index}>
              <DropIndicator
                isActive={dropIndex === index && draggedIndex !== null}
              />
              <ImpactCard
                isEditing={editingImpact === index}
                isDragging={draggedIndex === index}
                draggable={false}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                error={builderData.hasError(
                  `impact${index}` as keyof CreateFundraiserModel
                )}
              >
                <ImpactMainRow onClick={(e) => handleCardClick(e, index)}>
                  <DragHandle
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GripVertical size={16} />
                  </DragHandle>

                  <IconPreview color={impact.color.rgba || impact.color.hex}>
                    {renderIcon(
                      impact.icon,
                      impact.color.rgba || impact.color.hex
                    )}
                  </IconPreview>

                  <PreviewContent>
                    <PreviewNumber
                      color={impact.color.rgba || impact.color.hex}
                    >
                      {impact.number}
                    </PreviewNumber>
                    <PreviewDescription
                      color={impact.color.rgba || impact.color.hex}
                    >
                      {impact.description}
                    </PreviewDescription>
                  </PreviewContent>

                  <RemoveButton
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImpactByIndex(index);
                    }}
                  >
                    <X size={14} />
                  </RemoveButton>
                </ImpactMainRow>

                {editingImpact === index && (
                  <EditingSection>
                    <EditingTopRow>
                      <FormGroup>
                        <Label>Value</Label>
                        <Input
                          type="text"
                          value={impact.number}
                          onChange={(e) =>
                            updateImpact(index, "number", e.target.value)
                          }
                          placeholder="e.g., 785M, $50, 500"
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="text"
                          value={impact.description}
                          onChange={(e) =>
                            updateImpact(index, "description", e.target.value)
                          }
                          placeholder="e.g., People lack clean water"
                        />
                      </FormGroup>
                    </EditingTopRow>

                    <EditingBottomRow>
                      <FormGroup>
                        <Label>Icon</Label>
                        <FlexContainer spacing={10} alignItems="center">
                          <IconSelectorButton
                            onClick={(e) => {
                              e.stopPropagation();
                              openIconSelector(index);
                            }}
                          >
                            <IconPreview
                              color={impact.color.rgba || impact.color.hex}
                              style={{ width: "20px", height: "20px" }}
                            >
                              {renderIcon(
                                impact.icon,
                                impact.color.rgba || impact.color.hex
                              )}
                            </IconPreview>
                            <span>{impact.icon}</span>
                          </IconSelectorButton>
                        </FlexContainer>
                      </FormGroup>

                      <FormGroup className="position-relative">
                        <Label>Color</Label>
                        <FlexContainer
                          spacing={10}
                          alignItems="center"
                          className="position-relative"
                        >
                          <SelectedColorButton
                            heightWidth="24px"
                            borderRadius="2px"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowImpactColorPicker(true);
                            }}
                            color={impact.color.rgba || impact.color.hex}
                            className="color-picker-gallery"
                          >
                            <div />
                          </SelectedColorButton>
                          <ColorTextInput
                            type="text"
                            value={impact.color.hex || impact.color.rgba || ""}
                            onChange={(e) =>
                              updateImpact(
                                index,
                                "color",
                                new ColorModel({
                                  hex: e.target.value,
                                  rgba: "",
                                })
                              )
                            }
                            onBlur={(e) => {
                              updateImpact(
                                index,
                                "color",
                                ColorModel.fromString(e.target.value)
                              );
                            }}
                            placeholder="#3b82f6"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => onKeyDown?.(e, true)}
                          />
                          {showImpactColorPicker && (
                            <ColorPickerComponent
                              color={builderData.deserializeColor(impact.color)}
                              onChange={(color) =>
                                updateImpact(index, "color", color)
                              }
                              onHide={() => setShowImpactColorPicker(false)}
                              className="checkout-color-picker"
                            />
                          )}
                        </FlexContainer>
                      </FormGroup>
                    </EditingBottomRow>
                  </EditingSection>
                )}
              </ImpactCard>
            </React.Fragment>
          ))}

          <DropIndicator
            isActive={dropIndex === impacts.length && draggedIndex !== null}
          />

          <AddImpactButton onClick={addImpact}>
            <Plus size={24} />
            <AddImpactText>Add Impact</AddImpactText>
          </AddImpactButton>
        </ImpactGrid>

        <IconSelectorModal
          visible={showIconSelector}
          selectedIcon={
            iconSelectorIndex !== null
              ? impacts[iconSelectorIndex]?.icon || "Target"
              : "Target"
          }
          onClose={() => {
            setShowIconSelector(false);
            setIconSelectorIndex(null);
          }}
          onSelect={(iconName) => {
            if (iconSelectorIndex !== null) {
              handleIconSelect(iconSelectorIndex, iconName);
            }
          }}
        />
      </ImpactContainer>
    );
  }
);
